/**
 * An APA 7 table from pasted or typed data.
 *
 * The rules (APA Publication Manual, 7th ed., §§7.8–7.21, as reproduced by university
 * library guides from APA's table setup page):
 * - the table number in bold above the table, and one double-spaced line below it the
 *   title in italic title case;
 * - column headings for every column, the stub (leftmost) heading included; headings
 *   centered; stub entries flush left and other cells centered;
 * - "Numerical values should be centered in the column and aligned on the decimal";
 * - borders only where they aid clarity — at the top and bottom of the table, beneath
 *   the column headings, and optionally above a row of totals; "Do not use vertical
 *   borders to separate data, and do not use borders around every cell";
 * - notes below the table in a fixed order: the general note, introduced by the word
 *   "Note" in italics followed by a period; specific notes, marked by superscript
 *   lowercase letters; then probability notes (*p < .05. **p < .01. ***p < .001.).
 *
 * Decimal alignment survives a paste into Word or Google Docs because a numeric column
 * is split at the decimal point into two cells — the whole part right-aligned, the
 * decimals left-aligned — under one heading that spans both. No tab stops or special
 * spaces are needed, so it holds in any editor that keeps a table.
 */

import { escapeHtml } from "@/lib/tool-kit/citation-parts";
import {
  apaTitleCase,
  isEnglishLocale,
} from "@/lib/tool-kit/paper-formatting/apa-title-case";

// --- reading data -------------------------------------------------------------------

const RE_LINE_BREAK = /\r\n|\r|\n/;

/** Tab, then semicolon, then comma — whichever the first line uses. */
function delimiterOf(firstLine: string): string {
  for (const candidate of ["\t", ";", ","]) {
    if (firstLine.includes(candidate)) {
      return candidate;
    }
  }
  return "\t";
}

function splitRecord(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (quoted && char === '"' && line[i + 1] === '"') {
      cell += '"';
      i++;
    } else if (char === '"' && (quoted || cell.trim() === "")) {
      quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      cells.push(cell);
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell);
  return cells.map((value) => value.trim());
}

/**
 * Rows of cells from CSV, TSV (a spreadsheet paste) or semicolon-separated text.
 * Quoted cells may hold the delimiter. Rows are padded to the widest row.
 */
export function parseTableData(text: string): string[][] {
  // A line of tabs is an empty row from the grid editor, not a blank line.
  const lines = text
    .split(RE_LINE_BREAK)
    .filter((line) => line.trim() !== "" || line.includes("\t"));
  if (lines.length === 0) {
    return [];
  }
  const delimiter = delimiterOf(lines[0]);
  const rows = lines.map((line) => splitRecord(line, delimiter));
  const width = Math.max(...rows.map((row) => row.length));
  return rows.map((row) => [
    ...row,
    ...Array.from({ length: width - row.length }, () => ""),
  ]);
}

const RE_NEEDS_QUOTES = /[\t,;"\r\n]/;

/** Rows back to tab-separated text, quoting any cell that would break a row. */
export function rowsToTsv(rows: readonly (readonly string[])[]): string {
  return rows
    .map((row) =>
      row
        .map((cell) =>
          RE_NEEDS_QUOTES.test(cell) ? `"${cell.replaceAll('"', '""')}"` : cell
        )
        .join("\t")
    )
    .join("\n");
}

// --- numbers ------------------------------------------------------------------------

export interface SplitNumber {
  /** Sign, comparison and whole part: "−0", "<", "(12". */
  whole: string;
  /** Decimal point onward, with any closing mark: ".32**", ".45)". */
  part: string;
  stars: number;
}

const RE_NUMERIC =
  /^([−\-+±<>≤≥(]*)\s?(\d{1,3}(?:,\d{3})+|\d*)(\.\d+)?(%?\)?)(\*{0,3})((?:\^[a-z])?)$/;
// The same cell with a decimal comma: "6,84", "−,09", "<,001". No thousands grouping,
// because a comma here is the decimal mark.
const RE_NUMERIC_COMMA =
  /^([−\-+±<>≤≥(]*)\s?(\d*)(,\d+)?(%?\)?)(\*{0,3})((?:\^[a-z])?)$/;
// "1,234": a comma followed by exactly three digits reads as thousands in English and
// in the CJK and Hindi locales, and as a decimal fraction in German or Spanish. A column
// is only read with decimal commas when some cell settles it.
const RE_THOUSANDS_SHAPE = /^\D*[1-9]\d{0,2},\d{3}\D*$/;
const RE_HAS_COMMA = /,/;
const RE_DASH_CELL = /^[—–-]$/;
const RE_HYPHEN_MINUS = /-/g;

/**
 * A cell that is a number, split at its decimal point; null for anything else. With
 * `decimalComma` the comma is the decimal mark ("6,84") and the point is not.
 */
export function splitNumber(
  cell: string,
  decimalComma = false
): SplitNumber | null {
  const match = (decimalComma ? RE_NUMERIC_COMMA : RE_NUMERIC).exec(
    cell.trim()
  );
  if (!match) {
    return null;
  }
  const [, sign, whole, decimals = "", close, stars, marker] = match;
  if (whole === "" && decimals === "") {
    return null;
  }
  return {
    whole: sign.replace(RE_HYPHEN_MINUS, "−") + whole,
    part: decimals + close + stars + marker,
    stars: stars.length,
  };
}

function isPlaceholder(cell: string): boolean {
  const trimmed = cell.trim();
  return trimmed === "" || RE_DASH_CELL.test(trimmed);
}

// --- the model ----------------------------------------------------------------------

export interface ApaTableOptions {
  /** "Table" in the page's language. */
  tableWord: string;
  /** "Note" in the page's language. */
  noteWord: string;
  number: string;
  title: string;
  titleCase: boolean;
  /**
   * The page's language. English title case applies only to English; any other language
   * keeps the writer's capitalisation. Omitted means English.
   */
  locale?: string;
  decimalAlign: boolean;
  /** The last row holds totals and gets a border above it. */
  totalRow: boolean;
  generalNote: string;
  /** One per line: "a Text of the note." */
  specificNotes: string;
  probabilityNote: boolean;
}

export interface SpecificNote {
  letter: string;
  text: string;
}

export interface ApaTableModel {
  label: string;
  title: string;
  head: string[];
  body: string[][];
  /** Per column: split at the decimal point. */
  split: boolean[];
  /** Per column: the decimal mark is a comma ("6,84"), not a point. */
  decimalComma: boolean[];
  totalRow: boolean;
  noteWord: string;
  generalNote: string;
  specificNotes: SpecificNote[];
  /** 1 to 3: how many asterisk levels the probability note explains; 0 for none. */
  probabilityLevels: number;
}

const RE_SPECIFIC_NOTE = /^\^?([a-z])[.):]?\s+(\S.*)$/;
const RE_ENDS_WITH_STOP = /[.?!]$/;

export function parseSpecificNotes(text: string): SpecificNote[] {
  const notes: SpecificNote[] = [];
  for (const line of text.split(RE_LINE_BREAK)) {
    const match = RE_SPECIFIC_NOTE.exec(line.trim());
    if (match) {
      notes.push({ letter: match[1], text: match[2].trim() });
    }
  }
  return notes;
}

function withStop(text: string): string {
  const trimmed = text.trim();
  return trimmed === "" || RE_ENDS_WITH_STOP.test(trimmed)
    ? trimmed
    : `${trimmed}.`;
}

function columnNumbers(body: readonly string[][], column: number): string[] {
  return body
    .map((row) => row[column] ?? "")
    .filter((cell) => !isPlaceholder(cell));
}

/**
 * Columns whose numbers use a decimal comma, as a spreadsheet in German, French or
 * Spanish copies them. The safe rule: never in English; otherwise only a column in which
 * every number reads with a decimal comma, none has a point, and at least one cell is
 * not the thousands shape "1,234" — so a column of "1,234" and "5,678" stays as typed.
 */
function commaColumns(
  body: readonly string[][],
  width: number,
  locale: string | undefined
): boolean[] {
  return Array.from({ length: width }, (_, column) => {
    if (column === 0 || isEnglishLocale(locale)) {
      return false;
    }
    const numbers = columnNumbers(body, column);
    const withComma = numbers.filter((cell) => RE_HAS_COMMA.test(cell));
    return (
      withComma.length > 0 &&
      numbers.every((cell) => splitNumber(cell, true) !== null) &&
      withComma.some((cell) => !RE_THOUSANDS_SHAPE.test(cell.trim()))
    );
  });
}

function splitColumns(
  body: readonly string[][],
  width: number,
  comma: readonly boolean[]
): boolean[] {
  return Array.from({ length: width }, (_, column) => {
    if (column === 0) {
      return false;
    }
    const numbers = columnNumbers(body, column);
    return (
      numbers.length > 0 &&
      numbers.every((cell) => splitNumber(cell, comma[column]) !== null)
    );
  });
}

function maxStars(
  body: readonly string[][],
  comma: readonly boolean[]
): number {
  let most = 0;
  for (const row of body) {
    row.forEach((cell, column) => {
      most = Math.max(most, splitNumber(cell, comma[column])?.stars ?? 0);
    });
  }
  return most;
}

export function buildApaTable(
  rows: readonly string[][],
  options: ApaTableOptions
): ApaTableModel {
  const [head = [], ...body] = rows.map((row) => [...row]);
  const width = Math.max(head.length, ...body.map((row) => row.length), 0);
  const title =
    options.titleCase && isEnglishLocale(options.locale)
      ? apaTitleCase(options.title.trim())
      : options.title.trim();
  const decimalComma = commaColumns(body, width, options.locale);
  return {
    label: `${options.tableWord} ${options.number.trim() || "1"}`,
    title,
    head,
    body,
    split: options.decimalAlign
      ? splitColumns(body, width, decimalComma)
      : Array.from({ length: width }, () => false),
    decimalComma,
    totalRow: options.totalRow && body.length > 1,
    noteWord: options.noteWord,
    generalNote: withStop(options.generalNote),
    specificNotes: parseSpecificNotes(options.specificNotes).map((note) => ({
      ...note,
      text: withStop(note.text),
    })),
    probabilityLevels: options.probabilityNote
      ? maxStars(body, decimalComma)
      : 0,
  };
}

// --- output ------------------------------------------------------------------------

const PROBABILITY = [".05", ".01", ".001"];
const RULE = "1px solid #000";
const CELL = "padding:4px 8px;vertical-align:top";
const PARAGRAPH = "margin:0;line-height:2";
const RE_CELL_MARKUP = /\^([a-z])|_([^_\s][^_]*)_/g;
const SUPERSCRIPT_LETTERS: Record<string, string> = {
  a: "ᵃ",
  b: "ᵇ",
  c: "ᶜ",
  d: "ᵈ",
  e: "ᵉ",
  f: "ᶠ",
  g: "ᵍ",
  h: "ʰ",
  i: "ⁱ",
  j: "ʲ",
  k: "ᵏ",
  l: "ˡ",
  m: "ᵐ",
  n: "ⁿ",
  o: "ᵒ",
  p: "ᵖ",
  r: "ʳ",
  s: "ˢ",
  t: "ᵗ",
  u: "ᵘ",
  v: "ᵛ",
  w: "ʷ",
  x: "ˣ",
  y: "ʸ",
  z: "ᶻ",
};

export interface CellRun {
  text: string;
  style: "plain" | "sup" | "em";
}

/**
 * A cell's text in runs: "^a" marks a specific-note letter (superscript) and "_M_"
 * italicizes — APA sets statistical symbols such as M, SD, n and r in italics.
 */
export function cellRuns(text: string): CellRun[] {
  const runs: CellRun[] = [];
  let cursor = 0;
  for (const match of text.matchAll(RE_CELL_MARKUP)) {
    if (match.index > cursor) {
      runs.push({ text: text.slice(cursor, match.index), style: "plain" });
    }
    runs.push(
      match[1]
        ? { text: match[1], style: "sup" }
        : { text: match[2], style: "em" }
    );
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) {
    runs.push({ text: text.slice(cursor), style: "plain" });
  }
  return runs;
}

/** Cell text as HTML: escaped, with superscript letters and italics. */
function cellHtml(text: string): string {
  return cellRuns(text)
    .map((run) => {
      const safe = escapeHtml(run.text);
      if (run.style === "sup") {
        return `<sup>${safe}</sup>`;
      }
      return run.style === "em" ? `<em>${safe}</em>` : safe;
    })
    .join("");
}

/** Cell text as plain text: a note letter becomes a Unicode superscript where one exists. */
function cellText(text: string): string {
  return cellRuns(text)
    .map((run) =>
      run.style === "sup"
        ? (SUPERSCRIPT_LETTERS[run.text] ?? run.text)
        : run.text
    )
    .join("");
}

function borders(top: boolean, bottom: boolean): string {
  return `${top ? `;border-top:${RULE}` : ""}${bottom ? `;border-bottom:${RULE}` : ""}`;
}

function headHtml(model: ApaTableModel): string {
  const cells = model.head.map((cell, column) => {
    const span = model.split[column] ? ' colspan="2"' : "";
    return `<th${span} style="${CELL};font-weight:normal;text-align:center${borders(true, true)}">${cellHtml(cell)}</th>`;
  });
  return `<thead><tr>${cells.join("")}</tr></thead>`;
}

function bodyCellHtml(
  model: ApaTableModel,
  cell: string,
  column: number,
  edge: string
): string {
  if (!model.split[column]) {
    const align = column === 0 ? "left" : "center";
    return `<td style="${CELL};text-align:${align}${edge}">${cellHtml(cell)}</td>`;
  }
  const number = splitNumber(cell, model.decimalComma[column]);
  if (!number) {
    return `<td colspan="2" style="${CELL};text-align:center${edge}">${cellHtml(cell)}</td>`;
  }
  return (
    `<td style="${CELL};padding-right:0;text-align:right${edge}">${escapeHtml(number.whole)}</td>` +
    `<td style="${CELL};padding-left:0;text-align:left${edge}">${cellHtml(number.part)}</td>`
  );
}

function bodyHtml(model: ApaTableModel): string {
  const last = model.body.length - 1;
  const rows = model.body.map((row, index) => {
    const edge = borders(model.totalRow && index === last, index === last);
    const cells = model.head.map((_, column) =>
      bodyCellHtml(model, row[column] ?? "", column, edge)
    );
    return `<tr>${cells.join("")}</tr>`;
  });
  return `<tbody>${rows.join("")}</tbody>`;
}

/** "*", "**", "***" with their thresholds, for as many levels as the table uses. */
export function probabilityNotes(
  levels: number
): { stars: string; value: string }[] {
  return PROBABILITY.slice(0, levels).map((value, i) => ({
    stars: "*".repeat(i + 1),
    value,
  }));
}

function probabilityHtml(levels: number): string {
  return probabilityNotes(levels)
    .map((note) => `${note.stars}<em>p</em> &lt; ${note.value}.`)
    .join(" ");
}

function notesHtml(model: ApaTableModel): string {
  const parts: string[] = [];
  if (model.generalNote) {
    parts.push(
      `<p style="${PARAGRAPH}"><em>${escapeHtml(model.noteWord)}.</em> ${cellHtml(model.generalNote)}</p>`
    );
  }
  if (model.specificNotes.length > 0) {
    const notes = model.specificNotes
      .map((note) => `<sup>${note.letter}</sup> ${cellHtml(note.text)}`)
      .join(" ");
    parts.push(`<p style="${PARAGRAPH}">${notes}</p>`);
  }
  if (model.probabilityLevels > 0) {
    parts.push(
      `<p style="${PARAGRAPH}">${probabilityHtml(model.probabilityLevels)}</p>`
    );
  }
  return parts.join("");
}

/**
 * The table as Word- and Google Docs-ready HTML. Every border is an inline style on a
 * cell, because both editors drop stylesheet rules on paste but keep these.
 */
export function apaTableHtml(model: ApaTableModel): string {
  const caption =
    `<p style="${PARAGRAPH}"><strong>${escapeHtml(model.label)}</strong></p>` +
    `<p style="${PARAGRAPH}"><em>${escapeHtml(model.title)}</em></p>`;
  const table = `<table style="border-collapse:collapse;border:none">${headHtml(model)}${bodyHtml(model)}</table>`;
  return `${caption}${table}${notesHtml(model)}`;
}

// --- plain text ---------------------------------------------------------------------

const GAP = "   ";

function width(text: string): number {
  return [...text].length;
}

function center(text: string, size: number): string {
  const extra = Math.max(0, size - width(text));
  const left = Math.floor(extra / 2);
  return " ".repeat(left) + text + " ".repeat(extra - left);
}

interface TextColumn {
  size: number;
  whole: number;
  part: number;
}

function textColumns(model: ApaTableModel): TextColumn[] {
  return model.head.map((heading, column) => {
    let whole = 0;
    let part = 0;
    let plain = width(cellText(heading));
    for (const row of model.body) {
      const cell = cellText(row[column] ?? "");
      const number = model.split[column]
        ? splitNumber(row[column] ?? "", model.decimalComma[column])
        : null;
      if (number) {
        whole = Math.max(whole, width(number.whole));
        part = Math.max(part, width(cellText(number.part)));
      } else {
        plain = Math.max(plain, width(cell));
      }
    }
    return { size: Math.max(plain, whole + part), whole, part };
  });
}

function textCell(
  model: ApaTableModel,
  raw: string,
  column: number,
  layout: TextColumn
): string {
  const number = model.split[column]
    ? splitNumber(raw, model.decimalComma[column])
    : null;
  if (number) {
    const aligned =
      " ".repeat(layout.whole - width(number.whole)) +
      number.whole +
      cellText(number.part).padEnd(layout.part, " ");
    return center(aligned, layout.size);
  }
  const text = cellText(raw);
  return column === 0
    ? text.padEnd(layout.size, " ")
    : center(text, layout.size);
}

/** The table as plain text: columns padded with spaces, rules drawn with hyphens. */
export function apaTableText(model: ApaTableModel): string {
  const layout = textColumns(model);
  const line = (cells: string[]) => cells.join(GAP).trimEnd();
  const total = layout.reduce((sum, column) => sum + column.size, 0);
  const rule = "-".repeat(total + GAP.length * Math.max(0, layout.length - 1));
  const head = line(
    model.head.map((cell, column) =>
      column === 0
        ? cellText(cell).padEnd(layout[column].size, " ")
        : center(cellText(cell), layout[column].size)
    )
  );
  const body = model.body.map((row) =>
    line(
      model.head.map((_, column) =>
        textCell(model, row[column] ?? "", column, layout[column])
      )
    )
  );
  if (model.totalRow) {
    body.splice(body.length - 1, 0, rule);
  }
  const notes: string[] = [];
  if (model.generalNote) {
    notes.push(`${model.noteWord}. ${cellText(model.generalNote)}`);
  }
  if (model.specificNotes.length > 0) {
    notes.push(
      model.specificNotes
        .map(
          (note) =>
            `${SUPERSCRIPT_LETTERS[note.letter] ?? note.letter} ${cellText(note.text)}`
        )
        .join(" ")
    );
  }
  if (model.probabilityLevels > 0) {
    notes.push(
      probabilityNotes(model.probabilityLevels)
        .map((note) => `${note.stars}p < ${note.value}.`)
        .join(" ")
    );
  }
  return [
    model.label,
    model.title,
    rule,
    head,
    rule,
    ...body,
    rule,
    ...notes,
  ].join("\n");
}
